import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { PrimeNG } from 'primeng/config';
import { environment } from '@env/environment';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { ThirdPartyScriptsService } from '@shared/services/third-party-scripts/third-party-scripts.service';
import { CookieConsentService } from '@shared/services/cookie-consent/cookie-consent.service';
import { AnalyticsService } from '@shared/services/analytics/analytics.service';

/** Prefijo del título por ambiente; producción no se incluye para conservar su título. */
const ENVIRONMENT_TITLE_LABELS: Record<string, string> = {
    development: 'DEV',
    uat: 'UAT'
};

@Component({
    selector: 'app-root',
    imports: [CommonModule, RouterOutlet],
    templateUrl: './app.html',
})
export class App implements OnInit {
    env = environment;
    private readonly translateService = inject(TranslateService);
    private readonly thirdPartyScriptsService = inject(ThirdPartyScriptsService);
    private readonly cookieConsentService = inject(CookieConsentService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly primeng = inject(PrimeNG);
    private readonly destroyRef = inject(DestroyRef);
    private readonly router = inject(Router);
    private readonly title = inject(Title);

    /**
     * URL de la página cargada inicialmente (carga completa). GTM ya la cuenta
     * con el page_view automático del Google tag, por lo que no debe reenviarse
     * como page_view virtual. Se toma de window.location porque la navegación
     * inicial del router es no bloqueante y ocurre después de construir App.
     */
    private readonly initialUrl = this.getBrowserUrl();
    private lastTrackedUrl = this.initialUrl;

    constructor() {
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((event) => this.trackSpaNavigation(event.urlAfterRedirects));
    }

    ngOnInit() {
        this.setEnvironmentTitle();
        this.primeng.ripple.set(true);
        this.translate('es');
        if (this.cookieConsentService.allowsTracking()) {
            this.thirdPartyScriptsService.init(this.env.thirdPartyScripts);
        }
    }

    private setEnvironmentTitle(): void {
        const label = ENVIRONMENT_TITLE_LABELS[this.env.environment];
        if (label) {
            this.title.setTitle(`[${label}] ${this.title.getTitle()}`);
        }
    }

    private trackSpaNavigation(urlAfterRedirects: string): void {
        if (urlAfterRedirects === this.initialUrl || urlAfterRedirects === this.lastTrackedUrl) {
            return;
        }

        this.lastTrackedUrl = urlAfterRedirects;
        this.analyticsService.trackPageView(urlAfterRedirects.split('?')[0], `${window.location.origin}${urlAfterRedirects}`);
    }

    private getBrowserUrl(): string {
        if (typeof window === 'undefined') {
            return '';
        }

        return `${window.location.pathname}${window.location.search}`;
    }

    translate(lang: string) {
        this.translateService.use(lang);
        this.translateService.get('primeng').subscribe((res) => {
            this.primeng.setTranslation(res);
        });
    }
}
